import { zodResolver } from '@hookform/resolvers/zod'
import { Edit, CreditCard, ShieldAlert } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import z from 'zod'

import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog'
import { DiscreteLoader } from '../components/ui/discrete-loader'
import { Input } from '../components/ui/input'
import { TableSkeleton } from '../components/ui/table-skeleton'

import {
  storePaymentGatewayService,
  StorePaymentGateway,
} from '@/app/services/store-payment-methods.service'

const formSchema = z.object({
  is_active: z.boolean(),
  sandbox_mode: z.boolean(),
  allow_pickup: z.boolean(),
  allow_delivery: z.boolean(),
  merchant_id: z.string().optional(),
  private_key: z.string().optional(),
  public_key: z.string().optional(),
  access_token: z.string().optional(),
})

type FormValues = z.infer<typeof formSchema>

export function StorePaymentMethods() {
  const [data, setData] = useState<StorePaymentGateway[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [refreshCount, setRefreshCount] = useState(0)

  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<StorePaymentGateway | null>(null)
  const [isOpeningModal, setIsOpeningModal] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      is_active: false,
      sandbox_mode: true,
      allow_pickup: true,
      allow_delivery: true,
      merchant_id: '',
      private_key: '',
      public_key: '',
      access_token: '',
    },
  })

  const isActiveValue = watch('is_active')
  const isSandboxValue = watch('sandbox_mode')
  const allowPickupValue = watch('allow_pickup')
  const allowDeliveryValue = watch('allow_delivery')

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      try {
        const response = await storePaymentGatewayService.getItems()
        setData(response.data?.data || [])
      } catch {
        toast.error('Error al cargar las pasarelas de pago de la tienda')
      } finally {
        setIsLoading(false)
      }
    }
    fetchData()
  }, [refreshCount])

  const openEditModal = async (item: StorePaymentGateway) => {
    setIsOpeningModal(true)
    try {
      const response = await storePaymentGatewayService.getItems()
      const currentItems = response.data?.data || []
      const currentItem = currentItems.find((gateway) => gateway.id === item.id)
      if (!currentItem) {
        throw new Error('La pasarela ya no está disponible')
      }
      setData(currentItems)
      setEditingItem(currentItem)
      reset({
        is_active: currentItem.is_active,
        sandbox_mode: currentItem.sandbox_mode,
        allow_pickup: currentItem.allow_pickup,
        allow_delivery: currentItem.allow_delivery,
        merchant_id: currentItem.credentials?.merchant_id || '',
        private_key: '',
        public_key: currentItem.credentials?.public_key || '',
        access_token: '',
        })
      setShowModal(true)
    } catch {
      toast.error('No se pudo actualizar la configuración de la pasarela')
    } finally {
      setIsOpeningModal(false)
    }
  }

  const onSubmit = async (formData: FormValues) => {
    if (!editingItem) return

    try {
      let credentials = null
      if (editingItem.code !== 'cash') {
        credentials = editingItem.code === 'mercadopago'
          ? { merchant_id: formData.merchant_id || '', public_key: formData.public_key || '',
              ...(formData.access_token ? { access_token: formData.access_token } : {}) }
          : { merchant_id: formData.merchant_id || '', public_key: formData.public_key || '',
              ...(formData.private_key ? { private_key: formData.private_key } : {}) }
      }

      await storePaymentGatewayService.updateItem(editingItem.id, {
        is_active: formData.is_active,
        sandbox_mode: formData.sandbox_mode,
        allow_pickup: formData.allow_pickup,
        allow_delivery: formData.allow_delivery,
        credentials,
      })

      toast.success('Configuración guardada correctamente')
      setShowModal(false)
      setRefreshCount((prev) => prev + 1)
    } catch (error) {
      const details = (error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
        ?.response?.data
      toast.error(Object.values(details?.errors ?? {})[0]?.[0] ?? details?.message ?? 'Error al guardar la configuración')
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-header-foreground">Pasarelas de Pago de Tienda</h1>
          <p className="mt-1 text-sm text-slate-600">
            Gestiona los métodos de pago disponibles para tus clientes en la tienda en línea.
          </p>
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton
          headers={['Pasarela', 'Código', 'Modo', 'Entregas', 'Estado', 'Acciones']}
          columns={['text', 'text', 'badge', 'text', 'badge', 'actions']}
          rows={3}
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full whitespace-nowrap">
              <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
                <tr className="border-b border-border transition-colors hover:bg-table-row-hover">
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Pasarela</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Código</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Entorno</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Disponibilidad</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border bg-table-bg text-table-foreground">
                {data.length > 0 ? (
                  data.map((item) => (
                    <tr key={item.id} className="transition-colors hover:bg-table-row-hover">
                      <td className="px-6 py-4 font-medium">{item.name}</td>
                      <td className="px-6 py-4">{item.code}</td>
                      <td className="px-6 py-4">
                        {item.code !== 'cash' ? (
                          <Badge className={item.sandbox_mode ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}>
                            {item.sandbox_mode ? 'Sandbox / Pruebas' : 'Producción'}
                          </Badge>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-2">
                          {item.allow_delivery && <Badge className="bg-slate-100 text-slate-600">A Domicilio</Badge>}
                          {item.allow_pickup && <Badge className="bg-slate-100 text-slate-600">En Sucursal</Badge>}
                          {!item.allow_delivery && !item.allow_pickup && <span className="text-red-500">Ninguna</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <Badge className={item.is_active ? 'bg-[#DCFCE6] text-[#008438]' : 'bg-[#FFE2E2] text-[#C10008]'}>
                          {item.is_active ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => openEditModal(item)}
                          title="Configurar"
                          disabled={isOpeningModal}
                          className="rounded p-1.5 transition-colors hover:bg-muted"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <CreditCard className="mx-auto mb-3 h-8 w-8 text-slate-300" />
                      <p className="text-slate-500">No se encontraron pasarelas</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Configurar {editingItem?.name}</DialogTitle>
            <DialogDescription>
              Ajusta los parámetros para esta pasarela en tu tienda.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-4">
            <div className="flex flex-col gap-4 bg-slate-50 p-4 rounded-xl border">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="is_active"
                  checked={isActiveValue}
                  onCheckedChange={(v) => setValue('is_active', v === true, { shouldValidate: true })}
                />
                <label htmlFor="is_active" className="text-sm font-medium">
                  Habilitar este método de pago en la tienda
                </label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="allow_delivery"
                  checked={allowDeliveryValue}
                  onCheckedChange={(v) => setValue('allow_delivery', v === true, { shouldValidate: true })}
                />
                <label htmlFor="allow_delivery" className="text-sm font-medium">
                  Permitir usarlo en envíos a domicilio
                </label>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="allow_pickup"
                  checked={allowPickupValue}
                  onCheckedChange={(v) => setValue('allow_pickup', v === true, { shouldValidate: true })}
                />
                <label htmlFor="allow_pickup" className="text-sm font-medium">
                  Permitir usarlo en recolecciones en sucursal
                </label>
              </div>
            </div>

            {editingItem && editingItem.code !== 'cash' && (
              <div className="space-y-4 pt-2">
                <h4 className="text-sm font-bold flex items-center gap-2 text-slate-700">
                  <ShieldAlert className="w-4 h-4 text-amber-500" />
                  Credenciales API
                </h4>
                {editingItem.code === 'mercadopago' && <p className="text-xs text-slate-600">
                  Checkout Pro requiere Public Key y Access Token. Los pagos se verifican automáticamente mediante la API de Mercado Pago.
                </p>}

                <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <Checkbox
                    id="sandbox_mode"
                    checked={isSandboxValue}
                    onCheckedChange={(v) => setValue('sandbox_mode', v === true, { shouldValidate: true })}
                  />
                  <label htmlFor="sandbox_mode" className="text-sm font-medium text-amber-900">
                    Modo Sandbox (Modo Pruebas)
                  </label>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">Merchant ID {editingItem.code === 'mercadopago' ? '(opcional)' : ''}</label>
                  <Input type="text" {...register('merchant_id')} placeholder="ID de comercio..." />
                </div>
                {editingItem.code === 'mercadopago' ? <>
                <div>
                  <label className="mb-1 block text-sm font-medium">Access Token {editingItem.has_access_token ? '· configurado' : '· pendiente'}</label>
                  <Input type="password" autoComplete="new-password" {...register('access_token')} placeholder="Dejar vacío para conservarlo" />
                </div>
                </> : <div>
                  <label className="mb-1 block text-sm font-medium">Llave Privada (Private Key)</label>
                  <Input type="password" {...register('private_key')} placeholder={editingItem.has_private_key ? 'Configurada · dejar vacío para conservarla' : 'sk_test_...'} />
                </div>}
                <div>
                  <label className="mb-1 block text-sm font-medium">Llave Pública (Public Key)</label>
                  <Input type="text" {...register('public_key')} placeholder="pk_test_..." />
                </div>
              </div>
            )}

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)} disabled={isSubmitting}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isSubmitting} className="bg-primary hover:bg-primary/90">
                {isSubmitting ? <DiscreteLoader /> : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
